import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useRef } from 'react';
import { User, Product, Table, Company, UserRole, SubscriptionStatus, OrderItem, Payment, TableCloseResult, Transaction, AuditLogEntry, InviteCode, Manager, CashierShift, CashMovement, DeliveryOrder, DeliveryOrderStatus, ProductionSector } from '../types';
import { calculateTableTotal, generateOrderId } from '../services/dataService';
import { getNewOrderServiceFee, prepareTablePayment } from '../utils/serviceFee';
import { api, clearApiAuthToken, clearOwnerBaseSession, hasApiAuthToken, restoreOwnerBaseSession, setApiAuthToken, setApiCsrfToken } from '../services/api';

interface StoreContextType {
  user: User | null;
  currentCompany: Company | null;
  products: Product[];
  categories: string[];
  tables: Table[];
  dailyRevenue: number;
  transactions: Transaction[];
  auditLogs: AuditLogEntry[];
  deliveryOrders: DeliveryOrder[];
  allCompanies: Company[];
  lastTurnCloseTimestamp: number | null;
  cashierShift: CashierShift | null;
  login: (email: string, password: string, rememberMe: boolean) => Promise<{success: boolean, message?: string, role?: UserRole, subLoginEnabled?: boolean}>;
  loginAsWaiter: (companyEmail: string, waiterName: string, accessCode: string) => Promise<{success: boolean, message?: string}>;
  register: (companyName: string, ownerName: string, email: string, password: string, inviteCode: string) => Promise<{success: boolean, message?: string}>;
  logout: () => void;
  leaveActiveAccess: () => void;
  updateProfile: (name: string, password?: string, currentPassword?: string) => Promise<boolean>;
  updateContactInfo: (phone: string) => Promise<boolean>;
  updateTableCount: (count: number) => Promise<void>;
  addOrderToTable: (tableId: number, product: Product, quantity: number, notes: string) => void;
  addMultipleOrdersToTable: (tableId: number, items: { product: Product, quantity: number, notes: string, waiterName?: string }[], submissionId?: string) => Promise<boolean>;
  updateOrderItemQuantity: (tableId: number, itemId: string, change: number) => void;
  updateOrderItemDiscount: (tableId: number, itemId: string, discount: number) => void;
  updateOrderItemStatus: (tableId: number, itemId: string, status: OrderItem['status']) => void;
  updateOrderItemsStatus: (tableId: number, itemIds: string[], status: OrderItem['status']) => void;
  markOrderItemsPrinted: (tableId: number, itemIds: string[], sector: ProductionSector, reprint: boolean) => void;
  removeItemFromTable: (tableId: number, itemId: string) => void;
  requestBill: (tableId: number) => void;
  revertToOccupied: (tableId: number) => void;
  toggleServiceFee: (tableId: number) => void;
  setTableDiscount: (tableId: number, discount: number) => void;
  closeTable: (tableId: number, payments: Payment[]) => Promise<TableCloseResult>;
  cancelTable: (tableId: number) => Promise<{ restockedItemCount: number, lossItemCount: number }>;
  transferTable: (sourceId: number, destinationId: number, mergeWithDestination?: boolean) => Promise<{success: boolean, message: string}>;
  updateTableName: (tableId: number, name: string) => Promise<void>;
  addPartialPayments: (tableId: number, newPayments: Payment[]) => void;
  resetTablePayments: (tableId: number) => void;
  refreshData: () => Promise<void>;
  generateInviteCode: () => Promise<{success: boolean, code?: string, message?: string}>;
  deleteInviteCode: (id: string) => Promise<void>;
  getAdmins: () => Promise<User[]>;
  getActiveInvites: () => Promise<InviteCode[]>;
  verifyManagerAuth: (password: string) => Promise<{ user: User | Manager; type: 'owner' | 'manager' } | null>;
  addManager: (name: string, password: string) => Promise<{success: boolean, message: string}>;
  removeManager: (id: string) => Promise<void>;
  getManagers: () => Manager[];
  logManagerAction: (action: string, managerName: string) => Promise<void>;
  toggleCompanyStatus: (id: string, s: SubscriptionStatus) => Promise<boolean>;
  updateCompanyBillingDate: (id: string, d: string, activate?: boolean) => Promise<boolean>;
  deleteCompany: (id: string) => Promise<void>;
  startNewTurn: () => Promise<void>;
  adminUpdatePassword: (id: string, p: string) => Promise<boolean>;
  createAdmin: (n: string, e: string, p: string, l: 0|1|2, canSendEmails?: boolean, canLeadTeam?: boolean) => Promise<{success: boolean, message?: string}>;
  deleteAdmin: (id: string) => Promise<void>;
  addProduct: (product: Product) => Promise<void>;
  updateProduct: (product: Product) => Promise<void>;
  deleteProduct: (id: string) => Promise<void>;
  updateProductStock: (id: string, quantity: number, lowStockThreshold?: number) => Promise<void>;
  addProductStockBatch: (items: { productId: string; quantity: number }[]) => Promise<boolean>;
  addCategory: (categoryName: string) => Promise<void>;
  deleteCategory: (categoryName: string) => Promise<{success: boolean, message?: string}>;
  updateCompanySettings: (name: string, document: string, contactPhone?: string) => Promise<boolean>;
  checkSubscriptionStatus: () => Promise<void>;
  updateShiftSettings: (settings: { hour: number, minute: number, startHour: number, startMinute: number, useShift: boolean }) => Promise<boolean>;
  updateFeatureSettings: (settings: Partial<Pick<Company, 'useStockManagement' | 'useShiftManagement' | 'posMode' | 'useCounterMode' | 'useWaiterApp' | 'useKitchenDisplay' | 'useSplitBill' | 'useServiceFee' | 'useScale' | 'useDelivery' | 'deliverySettings' | 'useWhatsappAutomation' | 'useInvoicePhotoOcr' | 'useSubLogin' | 'waiterAccessCode'>>) => Promise<boolean>;
  createDeliveryOrder: (order: Omit<DeliveryOrder, 'id' | 'status' | 'createdAt' | 'updatedAt'>) => Promise<DeliveryOrder>;
  updateDeliveryOrderStatus: (orderId: string, status: DeliveryOrderStatus) => Promise<void>;
  receiveDeliveryPayment: (orderId: string) => Promise<void>;
  openCashier: (openingFloat: number, openingDetails?: any) => Promise<boolean>;
  addCashMovement: (movement: Omit<CashMovement, 'id' | 'timestamp'>) => Promise<boolean>;
  closeCashier: (report: any) => Promise<boolean>;
  updateClosedCashierReport: (shiftId: string, finalReport: any) => Promise<boolean>;
  getCashierHistory: () => Promise<CashierShift[]>;
}

const StoreContext = createContext<StoreContextType | undefined>(undefined);

const TABLE_SYNC_KEY = 'flowtab_cross_tab_sync_v1';
const DELIVERY_SYNC_KEY = 'flowtab_delivery_sync_v1';

const isLocalTestMode = () => {
    if (typeof window === 'undefined') return false;
    return ['localhost', '127.0.0.1'].includes(window.location.hostname);
};

const localCompanyOverridesKey = (companyId: string) => `ft_local_company_overrides_${companyId}`;

const getLocalCompanyOverrides = (companyId?: string): Partial<Company> => {
    if (!companyId || typeof window === 'undefined' || !isLocalTestMode()) return {};

    try {
        const overrides = JSON.parse(window.localStorage.getItem(localCompanyOverridesKey(companyId)) || '{}') as Partial<Company>;
        delete overrides.useSubLogin;
        return overrides;
    } catch {
        return {};
    }
};

const saveLocalCompanyOverrides = (companyId: string, settings: Partial<Company>) => {
    if (typeof window === 'undefined' || !isLocalTestMode()) return;

    const current = getLocalCompanyOverrides(companyId);
    const safeSettings = { ...settings };
    delete safeSettings.useSubLogin;
    window.localStorage.setItem(localCompanyOverridesKey(companyId), JSON.stringify({ ...current, ...safeSettings }));
};

const getLoadedTableServiceFee = (table: any, companyDefault: boolean) => {
    const hasItems = Array.isArray(table?.items) && table.items.length > 0;
    return hasItems ? table.serviceFee === true : companyDefault;
};

const deliveryOrdersKey = (companyId: string) => `ft_delivery_orders_${companyId}`;

const readLocalDeliveryOrders = (companyId?: string): DeliveryOrder[] => {
    if (!companyId || typeof window === 'undefined') return [];

    try {
        return JSON.parse(window.localStorage.getItem(deliveryOrdersKey(companyId)) || '[]');
    } catch {
        return [];
    }
};

const saveLocalDeliveryOrders = (companyId: string, orders: DeliveryOrder[]) => {
    if (typeof window === 'undefined') return;

    const leanOrders = orders.slice(0, 150);
    window.localStorage.setItem(deliveryOrdersKey(companyId), JSON.stringify(leanOrders));
    window.localStorage.setItem(DELIVERY_SYNC_KEY, JSON.stringify({ timestamp: Date.now(), companyId, data: leanOrders }));
};

const getUserFromStorage = (): User | null => {
    if (typeof window === 'undefined') return null;
    try {
        if (!hasApiAuthToken()) {
          const restoredOwner = restoreOwnerBaseSession();
          if (restoredOwner) return restoredOwner;
          if (window.localStorage.getItem('ft_user_session') || window.sessionStorage.getItem('ft_user_session')) {
            window.localStorage.setItem('ft_relogin_notice', '1');
          }
          window.localStorage.removeItem('ft_user_session');
          window.sessionStorage.removeItem('ft_user_session');
          return null;
        }

        // Prioriza a sessão persistente (localStorage)
        let item = window.localStorage.getItem('ft_user_session');
        if (item) {
          const safeUser = JSON.parse(item);
          delete safeUser.password;
          return safeUser;
        }

        // Se não houver, verifica a sessão temporária (sessionStorage)
        item = window.sessionStorage.getItem('ft_user_session');
        if (!item) return null;
        const safeUser = JSON.parse(item);
        delete safeUser.password;
        return safeUser;
    } catch (error) {
        console.warn(`Error reading user session from storage:`, error);
        return null;
    }
};

export const StoreProvider = ({ children }: { children?: ReactNode }) => {
  const [user, setUser] = useState<User | null>(getUserFromStorage);
  
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [tables, setTables] = useState<Table[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [deliveryOrders, setDeliveryOrders] = useState<DeliveryOrder[]>([]);
  const [managers, setManagers] = useState<Manager[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [allCompanies, setAllCompanies] = useState<Company[]>([]);
  const [lastTurnCloseTimestamp, setLastTurnCloseTimestamp] = useState<number | null>(null);
  const [cashierShift, setCashierShift] = useState<CashierShift | null>(null);
  const tableWriteQueue = useRef<Promise<boolean>>(Promise.resolve(true));
  const pendingTableWrites = useRef(0);
  const confirmedTables = useRef(new Map<number, Table>());
  const lastInteractionTime = useRef<number>(Date.now());

  useEffect(() => {
    const warnPendingWrites = (event: BeforeUnloadEvent) => {
      if (pendingTableWrites.current === 0) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnPendingWrites);
    return () => window.removeEventListener('beforeunload', warnPendingWrites);
  }, []);

  useEffect(() => {
    const handleAuthExpired = () => {
      setUser(null);
      setCurrentCompany(null);
      setProducts([]);
      setCategories([]);
      setTransactions([]);
      setDeliveryOrders([]);
      setTables([]);
      window.location.hash = '#/';
    };

    window.addEventListener('ft-auth-expired', handleAuthExpired);
    return () => window.removeEventListener('ft-auth-expired', handleAuthExpired);
  }, []);

  const setAndSyncTables = useCallback((updater: React.SetStateAction<Table[]>) => {
    setTables(prevTables => {
        const newTables = typeof updater === 'function' ? updater(prevTables) : updater;
        window.setTimeout(() => {
            try {
                const payload = { timestamp: Date.now(), companyId: user?.companyId, data: newTables };
                localStorage.setItem(TABLE_SYNC_KEY, JSON.stringify(payload));
            } catch (e) {
                console.error("Failed to sync tables to localStorage", e);
            }
        }, 0);
        return newTables;
    });
  }, [user?.companyId]);

  useEffect(() => {
    if (user?.id) {
        const storedTimestamp = localStorage.getItem(`ft_last_close_${user.id}`);
        setLastTurnCloseTimestamp(storedTimestamp ? parseInt(storedTimestamp, 10) : null);
    } else {
        setLastTurnCloseTimestamp(null);
    }
  }, [user?.id]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
        if (event.key === TABLE_SYNC_KEY && event.newValue) {
            try {
                const payload = JSON.parse(event.newValue);
                if (payload.companyId === user?.companyId && payload.data && pendingTableWrites.current === 0) {
                    setTables(prev => payload.data.map((incoming: Table) => {
                      const local = prev.find(table => table.id === incoming.id);
                      return local && Number(local.revision || 0) > Number(incoming.revision || 0) ? local : incoming;
                    }));
                    console.log('%c [Sync] State updated from another tab.', 'color: #0ea5e9');
                }
            } catch (e) {
                console.error("Failed to parse synced table state", e);
            }
        }

        if (event.key === DELIVERY_SYNC_KEY && event.newValue) {
            try {
                const payload = JSON.parse(event.newValue);
                if (payload.companyId === user?.companyId && payload.data) {
                    setDeliveryOrders(payload.data);
                    console.log('%c [Delivery] Pedidos atualizados por outra aba.', 'color: #f97316');
                }
            } catch (e) {
                console.error("Failed to parse synced delivery state", e);
            }
        }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [user?.companyId]);

  const refreshData = useCallback(async () => {
    if (!user) return;
    
    try {
        if (user.role === UserRole.SAAS_ADMIN) {
            const dbCompanies = await api.companies.getAll() || [];
            setAllCompanies(dbCompanies);
            return;
        }
        
        const companyId = user.companyId;
        if (!companyId) return;

        console.log(`%c [Sync] Buscando dados da nuvem para empresa: ${companyId}... `, 'color: #7c3aed; font-weight: bold');
        const [comp, orders, dbProducts, mgrs, dbCategories, logs, openTablesState, shiftStatus, dbDeliveryOrders] = await Promise.all([
            api.companies.getById(companyId),
            api.orders.getByCompany(companyId, 31),
            api.products.getByCompany(companyId),
            api.managers.getByCompany(companyId),
            api.categories.getByCompany(companyId),
            api.auditLogs.getByCompany(companyId),
            api.tables.getState(companyId),
            api.cashierShifts.getStatus(companyId),
            api.deliveryOrders.getByCompany(companyId, 7),
        ]);

        if (!comp || !openTablesState) return;
        setCurrentCompany({ ...comp, ...getLocalCompanyOverrides(companyId) });
        setTransactions(orders || []);
        setDeliveryOrders(dbDeliveryOrders || readLocalDeliveryOrders(companyId));
        setProducts(dbProducts || []);
        setManagers(mgrs || []);
        setAuditLogs(logs || []);
        setCashierShift(shiftStatus);

        const tableCount = comp?.tableCount || 0;
        const openTablesMap = new Map((openTablesState || []).map(t => [t.id, t]));
        
        let finalTables = Array.from({ length: tableCount }, (_, i) => {
            const id = i + 1;
            const persistedState = openTablesMap.get(id);
            // FIX: The status from the API is a generic string. Cast it to the specific type.
            return persistedState ? { ...(persistedState as any), id, total: calculateTableTotal(persistedState.items), discount: Number((persistedState as any).discount || 0), serviceFee: getLoadedTableServiceFee(persistedState, comp?.useServiceFee === true), status: persistedState.status as Table['status'] } : { id, label: `Mesa ${id}`, status: 'FREE', total: 0, discount: 0, serviceFee: comp?.useServiceFee === true, items: [], payments: [] };
        });

        const COUNTER_TABLE_ID = 0;
        if (comp?.useCounterMode) {
            const counterState = openTablesMap.get(COUNTER_TABLE_ID);
            const counterTable: Table = counterState 
                ? { ...(counterState as any), id: COUNTER_TABLE_ID, label: 'Balcão', total: calculateTableTotal((counterState as any).items), discount: Number((counterState as any).discount || 0), serviceFee: getLoadedTableServiceFee(counterState, comp?.useServiceFee === true), status: (counterState as any).status as Table['status'] } 
                : { id: COUNTER_TABLE_ID, label: 'Balcão', status: 'OCCUPIED', total: 0, discount: 0, serviceFee: comp?.useServiceFee === true, items: [], payments: [] };
            finalTables.unshift(counterTable); 
        }

        if (pendingTableWrites.current === 0) {
          tableWriteQueue.current = Promise.resolve(true);
          setAndSyncTables(previous => {
            const next = finalTables.map(table => {
              const local = previous.find(item => item.id === table.id);
              return local && Number(local.revision || 0) > Number(table.revision || 0) ? local : table as Table;
            });
            confirmedTables.current = new Map(next.map(table => [table.id, table]));
            return next;
          });
        }

        const catsFromProducts = dbProducts ? dbProducts.map(p => p.category) : [];
        const catsFromDb = dbCategories ? dbCategories.map(c => c.name) : [];
        setCategories(Array.from(new Set(['Geral', ...catsFromProducts, ...catsFromDb])).sort());

    } catch (e) { console.error("Erro crítico no refreshData:", e); }
  }, [user, setAndSyncTables]);

  const syncOpenTables = useCallback(async () => {
    if (pendingTableWrites.current > 0) return;
    if (Date.now() - lastInteractionTime.current < 4000) {
      return;
    }
    if (!user?.companyId || !currentCompany || document.hidden) return;

    const openTablesFromServer = await api.tables.getState(user.companyId);
    if (pendingTableWrites.current > 0 || Date.now() - lastInteractionTime.current < 4000) return;
    if (!openTablesFromServer) return;
    
    const tableCount = currentCompany.tableCount;
    const openTablesMap = new Map(openTablesFromServer.map(t => [t.id, t]));

    let newTables = Array.from({ length: tableCount }, (_, i) => {
        const id = i + 1;
        const persistedState = openTablesMap.get(id);
        return persistedState 
            ? { ...(persistedState as any), id, total: calculateTableTotal(persistedState.items), discount: Number((persistedState as any).discount || 0), serviceFee: getLoadedTableServiceFee(persistedState, currentCompany?.useServiceFee === true), status: persistedState.status as Table['status'] } 
            : { id, label: `Mesa ${id}`, status: 'FREE', total: 0, discount: 0, serviceFee: currentCompany?.useServiceFee === true, items: [], payments: [] };
    });

    if (currentCompany.useCounterMode) {
      const COUNTER_TABLE_ID = 0;
      const counterState = openTablesMap.get(COUNTER_TABLE_ID);
      const counterTable: Table = counterState 
          ? { ...(counterState as any), id: COUNTER_TABLE_ID, label: 'Balcão', total: calculateTableTotal((counterState as any).items), discount: Number((counterState as any).discount || 0), serviceFee: getLoadedTableServiceFee(counterState, currentCompany?.useServiceFee === true), status: (counterState as any).status as Table['status'] } 
          : { id: COUNTER_TABLE_ID, label: 'Balcão', status: 'OCCUPIED', total: 0, discount: 0, serviceFee: currentCompany?.useServiceFee === true, items: [], payments: [] };
      newTables.unshift(counterTable);
    }

    if (JSON.stringify(tables) !== JSON.stringify(newTables)) {
        confirmedTables.current = new Map(newTables.map(table => [table.id, table as Table]));
        setAndSyncTables(newTables);
    }
  }, [user, currentCompany, tables, setAndSyncTables]);

  useEffect(() => {
    if(user) {
        console.log('Usuário detectado, iniciando busca de dados...');
        refreshData();
    }
  }, [user, refreshData]);
  
  useEffect(() => {
    if (!user || user.role === UserRole.SAAS_ADMIN) return;

    const hasActiveTables = tables.some(table => table.id !== 0 && table.status !== 'FREE');
    const syncInterval = hasActiveTables ? 8000 : 20000;
    const intervalId = setInterval(syncOpenTables, syncInterval);
    const handleVisibleAgain = () => {
      if (!document.hidden) syncOpenTables();
    };
    window.addEventListener('focus', syncOpenTables);
    document.addEventListener('visibilitychange', handleVisibleAgain);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', syncOpenTables);
      document.removeEventListener('visibilitychange', handleVisibleAgain);
    };
  }, [user, syncOpenTables, tables]);

  const login = async (email: string, password: string, rememberMe: boolean) => {
    try {
        const result = await api.auth.login(email, password, rememberMe);
        if (result?.success && result.user) {
          if (result.user.role === UserRole.OWNER) {
            sessionStorage.removeItem('ft_subaccess_active');
          }
          setApiAuthToken(result.token, rememberMe);
          setApiCsrfToken(result.csrfToken);
          setUser(result.user);
          if (rememberMe) {
            localStorage.setItem('ft_user_session', JSON.stringify(result.user));
          } else {
            sessionStorage.setItem('ft_user_session', JSON.stringify(result.user));
          }
          return { success: true, role: result.user.role, subLoginEnabled: result.subLoginEnabled === true };
        }

        if (isLocalTestMode()) {
          const legacyUser = await api.users.getByEmail(email);
          if (legacyUser && legacyUser.password === password) {
            delete legacyUser.password;
            setUser(legacyUser);
            if (rememberMe) {
              localStorage.setItem('ft_user_session', JSON.stringify(legacyUser));
            } else {
              sessionStorage.setItem('ft_user_session', JSON.stringify(legacyUser));
            }
            return { success: true, role: legacyUser.role, subLoginEnabled: false };
          }
        }

        return { success: false, message: result?.message || 'Credenciais inválidas.' };
    } catch (e) { 
        console.error("Erro ao consultar MySQL no login:", e);
        return { success: false, message: 'Erro de conexão com o servidor.' };
    }
  };

  const loginAsWaiter = async (companyEmail: string, waiterName: string, accessCode: string) => {
    const result = await api.auth.waiterLogin(companyEmail, waiterName, accessCode);
    if (!result?.success) return { success: false, message: result?.message || 'Erro ao entrar como garçom.' };

    setApiAuthToken(result.token, false);
    setApiCsrfToken(result.csrfToken);
    setUser(result.user);
    setCurrentCompany(result.company);
    sessionStorage.setItem('ft_user_session', JSON.stringify(result.user));
    
    return { success: true };
  };

  const logout = () => {
    api.auth.logout?.().catch(() => null);
    setUser(null);
    setCurrentCompany(null);
    setProducts([]);
    setCategories([]);
    setAndSyncTables([]);
    setTransactions([]);
    setManagers([]);
    setAuditLogs([]);
    setCashierShift(null);
    clearApiAuthToken();
    clearOwnerBaseSession();
    localStorage.removeItem('ft_user_session');
    sessionStorage.removeItem('ft_user_session');
  };

  const saveTableState = (table: Table, submissionId?: string): Promise<boolean> => {
    const companyId = user?.companyId;
    const base = tables.find(item => item.id === table.id);
    if (!companyId || !base) return Promise.resolve(false);
    const baseState = JSON.parse(JSON.stringify(base));
    const desired = JSON.parse(JSON.stringify(table));
    pendingTableWrites.current += 1;
    const operation = tableWriteQueue.current.then(async () => {
      try {
        const result = await api.tables.updateState(desired, companyId, baseState, submissionId);
        confirmedTables.current.set(table.id, result.state);
        if (pendingTableWrites.current === 1) {
          setAndSyncTables(prev => prev.map(item => confirmedTables.current.get(item.id) || item));
        }
        return true;
      } catch (error: any) {
        setAndSyncTables(prev => prev.map(item => item.id === table.id ? confirmedTables.current.get(table.id) || base : item));
        alert(error.message || 'Alteração não confirmada. Confira a comanda.');
        return false;
      } finally {
        pendingTableWrites.current -= 1;
        if (pendingTableWrites.current === 0) await refreshData();
      }
    });
    tableWriteQueue.current = operation;
    return operation;
  };

  const getStockTarget = (product: Product) => {
    if (!product.trackStock && !product.stockProductId) return null;
    return {
      productId: product.stockProductId || product.id,
      quantityPerUnit: Math.max(0.001, Number(product.stockDeductQuantity || 1)),
      packageProductId: product.stockPackageProductId,
      packageSize: Math.max(0, Number(product.stockPackageSize || 0))
    };
  };

  const getStockTargets = (product: Product) => {
    const convertQuantity = (quantity: number, fromUnit: Product['stockUnit'], toUnit: Product['stockUnit']) => {
      if (!fromUnit || !toUnit || fromUnit === toUnit) return quantity;
      if (fromUnit === 'G' && toUnit === 'KG') return quantity / 1000;
      if (fromUnit === 'KG' && toUnit === 'G') return quantity * 1000;
      if (fromUnit === 'ML' && toUnit === 'L') return quantity / 1000;
      if (fromUnit === 'L' && toUnit === 'ML') return quantity * 1000;
      return null;
    };
    const recipeYield = Math.max(0.001, Number(product.recipeYield || 1));
    const ingredients = (product.stockIngredients || [])
      .filter(ingredient => ingredient.productId && Number(ingredient.quantity) > 0)
      .flatMap(ingredient => {
        const stockProduct = products.find(candidate => candidate.id === ingredient.productId);
        const converted = convertQuantity(Number(ingredient.quantity || 0), ingredient.unit, stockProduct?.stockUnit);
        if (converted === null) return [];
        return [{ productId: ingredient.productId, quantityPerUnit: Math.max(0.001, converted / recipeYield), packageProductId: undefined, packageSize: 0 }];
      });

    if (ingredients.length > 0) return ingredients;
    const legacyTarget = getStockTarget(product);
    return legacyTarget ? [legacyTarget] : [];
  };

  const reserveStockInMap = (
    availableStockMap: Map<string, number>,
    stockTarget: ReturnType<typeof getStockTarget>,
    quantity: number,
    productName: string
  ) => {
    if (!stockTarget) return true;

    const requiredQuantity = quantity * stockTarget.quantityPerUnit;
    let availableQuantity = availableStockMap.get(stockTarget.productId) || 0;

    if (availableQuantity < requiredQuantity && stockTarget.packageProductId && stockTarget.packageSize > 0) {
      const missingQuantity = requiredQuantity - availableQuantity;
      const packagesToOpen = Math.ceil(missingQuantity / stockTarget.packageSize);
      const packageAvailable = availableStockMap.get(stockTarget.packageProductId) || 0;

      if (packageAvailable >= packagesToOpen) {
        availableStockMap.set(stockTarget.packageProductId, packageAvailable - packagesToOpen);
        availableQuantity += packagesToOpen * stockTarget.packageSize;
      }
    }

    if (availableQuantity < requiredQuantity) {
      alert(`Estoque insuficiente para "${productName}". Disponível: ${availableQuantity}.`);
      return false;
    }

    availableStockMap.set(stockTarget.productId, availableQuantity - requiredQuantity);
    return true;
  };

  const reserveStockTargetsInMap = (
    availableStockMap: Map<string, number>,
    stockTargets: ReturnType<typeof getStockTargets>,
    quantity: number,
    productName: string
  ) => {
    if (stockTargets.length === 0) return true;
    const testMap = new Map(availableStockMap);

    for (const stockTarget of stockTargets) {
      if (!reserveStockInMap(testMap, stockTarget, quantity, productName)) return false;
    }

    testMap.forEach((value, key) => availableStockMap.set(key, value));
    return true;
  };

  const applyAvailableStockMap = (availableStockMap: Map<string, number>) => {
    setProducts(prev => prev.map(product => {
      if (!availableStockMap.has(product.id)) return product;
      const nextQuantity = Math.max(0, availableStockMap.get(product.id) || 0);
      return Number(product.stockQuantity || 0) === nextQuantity
        ? product
        : { ...product, stockQuantity: nextQuantity };
    }));
  };

  const leaveActiveAccess = () => {
    sessionStorage.removeItem('ft_subaccess_active');
    if (user?.role === UserRole.OWNER) {
      window.location.hash = '#/access';
      return;
    }

    clearApiAuthToken();
    const owner = restoreOwnerBaseSession();
    if (owner) {
      setUser(owner);
      window.location.hash = '#/access';
      return;
    }

    logout();
  };

  const applyLocalOrderItemStockDelta = (items: OrderItem[], direction: -1 | 1) => {
    setProducts(prev => prev.map(product => {
      const totalDelta = items.reduce((sum, item) => {
        const sourceProduct = prev.find(prod => prod.id === item.productId);
        const stockTargets = item.stockIngredients?.length
          ? item.stockIngredients.map(ingredient => ({ productId: ingredient.productId, quantityPerUnit: Math.max(0.001, Number(ingredient.quantity || 1) / Math.max(0.001, Number(item.stockRecipeYield || sourceProduct?.recipeYield || 1))) }))
          : sourceProduct
            ? getStockTargets(sourceProduct)
            : item.stockProductId
              ? [{ productId: item.stockProductId, quantityPerUnit: Math.max(0.001, Number(item.stockDeductQuantity || 1)) }]
              : [];

        return sum + stockTargets.reduce((targetSum, target) => {
          if (target.productId !== product.id) return targetSum;
          return targetSum + (item.quantity * target.quantityPerUnit * direction);
        }, 0);
      }, 0);

      if (!totalDelta) return product;
      return {
        ...product,
        stockQuantity: Math.max(0, Number(product.stockQuantity || 0) + totalDelta)
      };
    }));
  };

  const addMultipleOrdersToTable = async (tableId: number, cartItems: { product: Product, quantity: number, notes: string, waiterName?: string }[], submissionId?: string) => {
    lastInteractionTime.current = Date.now();
    const table = tables.find(t => t.id === tableId);
    if (!table) return false;

    let newItems = [...table.items];
    const availableStockMap = new Map(products.map(product => [product.id, Number(product.stockQuantity || 0)]));
    let addedAnyItem = false;

    for (const cartItem of cartItems) {
      const { product, quantity, notes, waiterName } = cartItem;
      const stockTargets = getStockTargets(product);
      if (!reserveStockTargetsInMap(availableStockMap, stockTargets, quantity, product.name)) return false;

      const canMerge = !notes;
      const existingItemIndex = canMerge ? newItems.findIndex(i => i.productId === product.id && !i.notes) : -1;
      if (existingItemIndex > -1) {
        newItems[existingItemIndex] = { ...newItems[existingItemIndex], quantity: newItems[existingItemIndex].quantity + quantity };
      } else {
        const newItem: OrderItem = {
          id: generateOrderId(),
          productId: product.id,
          productName: product.name,
          quantity,
          price: product.price,
          notes: notes || '',
          waiterName,
          sentAt: new Date(),
          productionArea: product.productionArea || 'AUTO',
          saleUnit: product.saleUnit || 'UNIT',
          stockProductId: stockTargets[0]?.productId,
          stockDeductQuantity: stockTargets[0]?.quantityPerUnit,
          stockIngredients: product.stockIngredients,
          stockRecipeYield: product.recipeYield,
          stockPackageProductId: stockTargets[0]?.packageProductId,
          stockPackageSize: stockTargets[0]?.packageSize,
          status: 'PENDING'
        };
        newItems.push(newItem);
      }
      addedAnyItem = true;
    }

    if (!addedAnyItem) return false;

    const updatedTable = { ...table, items: newItems, total: calculateTableTotal(newItems), serviceFee: getNewOrderServiceFee(table, currentCompany?.useServiceFee === true), serviceFeeChosen: table.items.length === 0 && table.payments.length === 0 ? false : table.serviceFeeChosen, status: 'OCCUPIED' as const };
    applyAvailableStockMap(availableStockMap);
    setAndSyncTables(prev => prev.map(t => t.id === tableId ? updatedTable : t));
    return saveTableState(updatedTable, submissionId);
  };

  const cancelTable = async (tableId: number) => {
    lastInteractionTime.current = Date.now();
    const table = tables.find(t => t.id === tableId);
    if (!table || !user?.companyId) throw new Error('Comanda não encontrada.');
    if (!await tableWriteQueue.current) throw new Error('Há alteração não confirmada. Confira a mesa antes de cancelar.');
    const result = await api.tables.cancel(confirmedTables.current.get(tableId) || table, user.companyId);
    if (!result) throw new Error('Cancelamento não confirmado pelo servidor.');
    confirmedTables.current.set(tableId, result.table);
    setAndSyncTables(prev => prev.map(item => item.id === tableId ? result.table : item));
    await refreshData();
    return { restockedItemCount: result.restockedItemCount, lossItemCount: result.lossItemCount };
  };

  const transferTable = async (sourceId: number, destinationId: number, mergeWithDestination = false) => {
    if (!user?.companyId) return { success: false, message: 'Sessão inválida.' };
    if (!await tableWriteQueue.current) return { success: false, message: 'Há alteração não confirmada. Confira a mesa antes de transferir.' };

    const sourceTable = tables.find(t => t.id === sourceId);
    const destinationTable = tables.find(t => t.id === destinationId);

    if (!sourceTable || !destinationTable) {
        return { success: false, message: 'Mesa de origem ou destino não encontrada.' };
    }
    if (sourceId === destinationId) {
        return { success: false, message: 'A mesa de origem e destino não podem ser a mesma.' };
    }
    if (sourceTable.items.length === 0) {
        return { success: false, message: 'Mesa de origem está vazia e não pode ser movida.' };
    }
    if (destinationTable.status !== 'FREE' && !mergeWithDestination) {
        return { success: false, message: `A Mesa ${destinationId} já está ocupada.` };
    }

    let result;
    try {
      result = await api.tables.transfer(user.companyId, sourceId, destinationId, mergeWithDestination, confirmedTables.current.get(sourceId) || sourceTable, confirmedTables.current.get(destinationId) || destinationTable);
    } catch (error: any) {
      await refreshData();
      return { success: false, message: error.message };
    }
    if (!result) {
      return { success: false, message: 'Não foi possível confirmar a transferência. Atualize as mesas antes de tentar novamente.' };
    }

    lastInteractionTime.current = Date.now();
    confirmedTables.current.set(sourceId, result.source);
    confirmedTables.current.set(destinationId, result.destination);
    setAndSyncTables(prev => prev.map(table => {
      if (table.id === sourceId) return result.source;
      if (table.id === destinationId) return result.destination;
      return table;
    }));
    
    return {
      success: true,
      message: result.message
    };
  };

  const updateTableName = async (tableId: number, name: string) => {
    if (!user?.companyId) return;
    const table = tables.find(t => t.id === tableId);
    if (!table) return;

    const cleanName = name.trim().slice(0, 40);
    const updatedTable: Table = { ...table, name: cleanName || undefined };

    lastInteractionTime.current = Date.now();
    setAndSyncTables(prev => prev.map(t => t.id === tableId ? updatedTable : t));

    await saveTableState(updatedTable);
  };
    const addPartialPayments = (tableId: number, newPayments: Payment[]) => {
    lastInteractionTime.current = Date.now();
    const table = tables.find(t => t.id === tableId);
    if (!table) return;

    const updatedTable: Table = {
        ...table,
        payments: [...(table.payments || []), ...newPayments],
        status: 'OCCUPIED',
    };

    setAndSyncTables(prev => prev.map(t => t.id === tableId ? updatedTable : t));
    saveTableState(updatedTable);
  };

  const resetTablePayments = (tableId: number) => {
    lastInteractionTime.current = Date.now();
    const table = tables.find(t => t.id === tableId);
    if (!table) return;

    const updatedTable: Table = {
      ...table,
      payments: [],
      status: table.items.length > 0 ? 'PAYING' : 'FREE',
    };

    setAndSyncTables(prev => prev.map(t => t.id === tableId ? updatedTable : t));
    saveTableState(updatedTable);
  };
  
  const startNewTurn = async () => {
    if (user?.id && user.companyId) {
      if (tables.some(table => table.items.length || table.payments.length)) throw new Error('Encerre as comandas antes de iniciar outro turno.');
      // Clear all open tables before closing the turn
      setAndSyncTables(prev => prev.map(t => 
        t.status === 'FREE' ? t : { ...t, name: undefined, items: [], total: 0, discount: 0, payments: [], status: 'FREE', openedAt: undefined, serviceFee: false }
      ));

      const now = Date.now();
      setLastTurnCloseTimestamp(now);
      localStorage.setItem(`ft_last_close_${user.id}`, now.toString());
      await refreshData();
    }
  };

  const createDeliveryOrder = async (order: Omit<DeliveryOrder, 'id' | 'status' | 'createdAt' | 'updatedAt'>) => {
    const now = new Date().toISOString();
    const newOrder: DeliveryOrder = {
      ...order,
      id: `del-${generateOrderId()}`,
      status: 'RECEIVED',
      createdAt: now,
      updatedAt: now,
    };

    const savedOrder = user ? await api.deliveryOrders.save(newOrder) : await api.deliveryOrders.savePublic(newOrder);
    if (savedOrder) {
      setDeliveryOrders(prev => [savedOrder, ...prev.filter(order => order.id !== savedOrder.id)]);
      saveLocalDeliveryOrders(newOrder.companyId, [savedOrder, ...readLocalDeliveryOrders(newOrder.companyId).filter(order => order.id !== savedOrder.id)]);
      return savedOrder;
    }

    throw new Error('Não foi possível confirmar o pedido no servidor.');
  };

  const updateDeliveryOrderStatus = async (orderId: string, status: DeliveryOrderStatus) => {
    const targetCompanyId = user?.companyId || currentCompany?.id;
    const savedOrder = await api.deliveryOrders.updateStatus(orderId, status);
    if (!savedOrder || (savedOrder as any).requestError) {
      alert('Status não confirmado pelo servidor. O pedido não foi alterado.');
      return;
    }
    setDeliveryOrders(prev => {
      const next = prev.map(order => order.id === orderId ? (savedOrder || { ...order, status, updatedAt: new Date().toISOString() }) : order);
      const companyId = targetCompanyId || next.find(order => order.id === orderId)?.companyId;
      if (companyId) saveLocalDeliveryOrders(companyId, next);
      return next;
    });
  };

  const receiveDeliveryPayment = async (orderId: string) => {
    const targetCompanyId = user?.companyId || currentCompany?.id;
    const savedOrder = await api.deliveryOrders.receivePayment(orderId);
    if (!savedOrder || (savedOrder as any).requestError) {
      alert('Pagamento não confirmado pelo servidor.');
      return;
    }
    setDeliveryOrders(prev => {
      const next = prev.map(order => order.id === orderId ? savedOrder : order);
      const companyId = targetCompanyId || next.find(order => order.id === orderId)?.companyId;
      if (companyId) saveLocalDeliveryOrders(companyId, next);
      return next;
    });
    await refreshData();
  };

  return (
    <StoreContext.Provider value={{
      user, currentCompany, products, categories, tables, dailyRevenue: transactions.reduce((a, b) => a + b.total, 0), transactions, auditLogs, deliveryOrders, allCompanies,
      lastTurnCloseTimestamp, cashierShift, login, logout, leaveActiveAccess, refreshData, addMultipleOrdersToTable, cancelTable, addPartialPayments, resetTablePayments, startNewTurn, transferTable,
      updateTableName,
      createDeliveryOrder, receiveDeliveryPayment,
      updateDeliveryOrderStatus,
      loginAsWaiter,
      register: async (companyName, ownerName, email, password, inviteCode) => {
          const result = await api.register({ companyName, ownerName, email, password, inviteCode: inviteCode.trim().toUpperCase() });
          return {
              success: result?.success || false,
              message: result?.message || 'Erro desconhecido durante o registro.'
          };
      },
      updateProfile: async (n, p, currentPassword) => {
        if (!user) return false;
        const updated = { ...user, name: n, ...(p ? { password: p, currentPassword } : {}) };
        const savedUser = await api.users.save(updated);
        if (!savedUser) return false;
        setUser(savedUser);
        if (localStorage.getItem('ft_user_session')) localStorage.setItem('ft_user_session', JSON.stringify(savedUser));
        else sessionStorage.setItem('ft_user_session', JSON.stringify(savedUser));
        return true;
      },
      updateContactInfo: async (phone) => {
        if (!user) return false;
        const cleanPhone = phone.trim();
        const savedUser = user.role !== UserRole.WAITER
          ? await api.users.save({ ...user, phone: cleanPhone })
          : user;
        if (!savedUser) return false;
        setUser(savedUser);
        if (localStorage.getItem('ft_user_session')) localStorage.setItem('ft_user_session', JSON.stringify(savedUser));
        else sessionStorage.setItem('ft_user_session', JSON.stringify(savedUser));

        if (user.companyId && currentCompany) {
          const savedCompany = await api.companies.updateSettings(currentCompany.id, { contactPhone: cleanPhone });
          if (savedCompany) setCurrentCompany(savedCompany);
        }
        return true;
      },
      updateTableCount: async (count) => {
        if (!user?.companyId || !currentCompany) return;
        
        // PERFORMANCE FIX: Optimistic UI Update. Avoids full refreshData().
        const openTablesMap = new Map(tables.filter(t => t.status !== 'FREE').map(t => [t.id, t]));
        
        // FIX: Explicitly type `newTables` as `Table[]` to avoid type inference issues with the `status` property.
        // Fix: Added explicit return type `Table` to the map function to ensure correct type inference.
        let newTables: Table[] = Array.from({ length: count }, (_, i): Table => {
            const id = i + 1;
            const persistedState = openTablesMap.get(id);
            return persistedState ? persistedState : { id, label: `Mesa ${id}`, status: 'FREE', total: 0, serviceFee: currentCompany?.useServiceFee === true, items: [], payments: [] };
        });
      
        if (currentCompany.useCounterMode) {
            // FIX: Explicitly type 'counterTable' to prevent potential type inference issues.
            const counterTable: Table = tables.find(t => t.id === 0) || { id: 0, label: 'Balcão', status: 'OCCUPIED', total: 0, serviceFee: currentCompany?.useServiceFee === true, items: [], payments: [] };
            newTables.unshift(counterTable);
        }
        
        setAndSyncTables(newTables);
        setCurrentCompany(prev => prev ? ({...prev, tableCount: count}) : null);
        
        // Update server in the background
        await api.companies.updateTableCount(user.companyId, count);
      },
      addOrderToTable: (tableId, product, quantity, notes) => {
        addMultipleOrdersToTable(tableId, [{ product, quantity, notes }]);
      },
      updateOrderItemQuantity: (tableId, itemId, change) => {
        lastInteractionTime.current = Date.now();
        const table = tables.find(t => t.id === tableId);
        if (!table) return;
        const targetItem = table.items.find(item => item.id === itemId);

        if (targetItem && change !== 0) {
          if (change > 0) {
            const sourceProduct = products.find(product => product.id === targetItem.productId);
            const stockTargets = targetItem.stockIngredients?.length
              ? targetItem.stockIngredients.map(ingredient => ({
                  productId: ingredient.productId,
                  quantityPerUnit: Math.max(0.001, Number(ingredient.quantity || 1) / Math.max(0.001, Number(targetItem.stockRecipeYield || sourceProduct?.recipeYield || 1))),
                  packageProductId: undefined,
                  packageSize: 0
                }))
              : sourceProduct
                ? getStockTargets(sourceProduct)
                : targetItem.stockProductId
                  ? [{
                      productId: targetItem.stockProductId,
                      quantityPerUnit: Math.max(0.001, Number(targetItem.stockDeductQuantity || 1)),
                      packageProductId: targetItem.stockPackageProductId,
                      packageSize: Math.max(0, Number(targetItem.stockPackageSize || 0))
                    }]
                  : [];
            const availableStockMap = new Map(products.map(product => [product.id, Number(product.stockQuantity || 0)]));
            if (!reserveStockTargetsInMap(availableStockMap, stockTargets, Math.abs(change), targetItem.productName)) return;
            applyAvailableStockMap(availableStockMap);
          } else {
            applyLocalOrderItemStockDelta([{ ...targetItem, quantity: Math.abs(change) }], 1);
          }
        }

        const items = table.items.map(i => i.id === itemId ? { ...i, quantity: Math.max(0, i.quantity + change) } : i).filter(i => i.quantity > 0);
        const updatedTable = { ...table, items, total: calculateTableTotal(items), status: items.length === 0 ? 'FREE' : table.status };
        setAndSyncTables(prev => prev.map(t => t.id === tableId ? updatedTable : t));
        saveTableState(updatedTable);
      },
      updateOrderItemDiscount: (tableId, itemId, discount) => {
        lastInteractionTime.current = Date.now();
        const table = tables.find(t => t.id === tableId);
        if (!table) return;

        const items = table.items.map(item => {
          if (item.id !== itemId) return item;
          const safeDiscount = Math.min(Math.max(0, Number(discount || 0)), Number(item.price || 0));
          return { ...item, discount: safeDiscount };
        });
        const total = calculateTableTotal(items);
        const updatedTable = { ...table, items, total, discount: Math.min(Number(table.discount || 0), total) };
        setAndSyncTables(prev => prev.map(t => t.id === tableId ? updatedTable : t));
        saveTableState(updatedTable);
      },
      updateOrderItemStatus: (tableId, itemId, status) => {
        lastInteractionTime.current = Date.now();
        const table = tables.find(t => t.id === tableId);
        if (!table) return;
        const items = table.items.map(item => item.id === itemId ? { ...item, status } : item);
        const updatedTable = { ...table, items };
        setAndSyncTables(prev => prev.map(t => t.id === tableId ? updatedTable : t));
        saveTableState(updatedTable);
      },
      updateOrderItemsStatus: (tableId, itemIds, status) => {
        lastInteractionTime.current = Date.now();
        const table = tables.find(t => t.id === tableId);
        if (!table || itemIds.length === 0) return;
        const selectedItemIds = new Set(itemIds);
        const items = table.items.map(item => selectedItemIds.has(item.id) ? { ...item, status } : item);
        const updatedTable = { ...table, items };
        setAndSyncTables(prev => prev.map(t => t.id === tableId ? updatedTable : t));
        saveTableState(updatedTable);
      },
      markOrderItemsPrinted: (tableId, itemIds, sector, reprint) => {
        const table = tables.find(t => t.id === tableId);
        if (!table || itemIds.length === 0) return;
        const selectedItemIds = new Set(itemIds);
        const printedAt = new Date();
        const items = table.items.map(item => {
          if (!selectedItemIds.has(item.id)) return item;
          const previousMark = item.productionPrints?.[sector];
          return {
            ...item,
            productionPrints: {
              ...item.productionPrints,
              [sector]: { printedAt, reprintCount: (previousMark?.reprintCount || 0) + (reprint ? 1 : 0) },
            },
          };
        });
        const updatedTable = { ...table, items };
        setAndSyncTables(prev => prev.map(t => t.id === tableId ? updatedTable : t));
        saveTableState(updatedTable);
      },
      removeItemFromTable: (tableId, itemId) => {
        lastInteractionTime.current = Date.now();
        const table = tables.find(t => t.id === tableId);
        if (!table) return;
        const removedItem = table.items.find(i => i.id === itemId);
        if (removedItem) applyLocalOrderItemStockDelta([removedItem], 1);
        const items = table.items.filter(i => i.id !== itemId);
        const updatedTable = { ...table, items, total: calculateTableTotal(items), status: items.length === 0 ? 'FREE' : table.status };
        setAndSyncTables(prev => prev.map(t => t.id === tableId ? updatedTable : t));
        saveTableState(updatedTable);
      },
      requestBill: (id) => {
        lastInteractionTime.current = Date.now();
        const table = tables.find(t => t.id === id);
        if (table) {
          const updatedTable = prepareTablePayment(table, currentCompany?.useServiceFee === true);
          setAndSyncTables(prev => prev.map(t => t.id === id ? updatedTable : t));
          saveTableState(updatedTable);
        }
      },
      revertToOccupied: (id) => {
        lastInteractionTime.current = Date.now();
        const table = tables.find(t => t.id === id);
        if (table) {
          const updatedTable = {...table, status: 'OCCUPIED' as const};
          setAndSyncTables(prev => prev.map(t => t.id === id ? updatedTable : t));
          saveTableState(updatedTable);
        }
      },
      toggleServiceFee: (id) => {
        lastInteractionTime.current = Date.now();
        const table = tables.find(t => t.id === id);
        if (table) {
          const updatedTable = {...table, serviceFee: !table.serviceFee, serviceFeeChosen: true};
          setAndSyncTables(prev => prev.map(t => t.id === id ? updatedTable : t));
          saveTableState(updatedTable);
        }
      },
      setTableDiscount: (id, discount) => {
        lastInteractionTime.current = Date.now();
        const table = tables.find(t => t.id === id);
        if (table) {
          const safeDiscount = Math.min(Math.max(0, Number(discount || 0)), Number(table.total || 0));
          const updatedTable = {...table, discount: safeDiscount};
          setAndSyncTables(prev => prev.map(t => t.id === id ? updatedTable : t));
          saveTableState(updatedTable);
        }
      },
      closeTable: async (tableId, newPayments) => {
        if (!await tableWriteQueue.current) throw new Error('Há alteração não confirmada. Confira a mesa antes de finalizar.');
        const table = tables.find(t => t.id === tableId);
        if (!table || !user?.companyId) return;
        const result = await api.tables.close(confirmedTables.current.get(tableId) || table, user.companyId, newPayments);
        setTransactions(prev => [result.order, ...prev.filter(order => order.id !== result.order.id)]);
        confirmedTables.current.set(tableId, result.table);
        setAndSyncTables(prev => prev.map(item => item.id === tableId ? result.table : item));
        return result;
      },
      generateInviteCode: async () => {
        if (!user) return { success: false, message: 'Usuário não autenticado.' };
        return await api.invites.generate(user.id, user.name)
          .then(res => res ? { success: true, code: res.code } : { success: false, message: 'Erro no banco.' })
          .catch(() => ({ success: false, message: 'Erro de comunicação.' }));
      },
      deleteInviteCode: async (id: string) => {
        await api.invites.delete(id);
      },
      getAdmins: async () => await api.users.getAdmins() || [],
      getActiveInvites: async () => await api.invites.getActive() || [],
      verifyManagerAuth: async (p) => {
        const result = await api.auth.verifyManager(p);
        if (!result?.success) {
          if (isLocalTestMode() && p === 'test' && user) return { user, type: 'owner' };
          return null;
        }
        return { user: result.user, type: result.type };
      },
      addManager: async (n, p) => {
        if (!user?.companyId) return { success: false, message: 'Sessão inválida' };
        const newManager = await api.managers.create(n, p, user.companyId);
        if (newManager) {
          setManagers(prev => [...prev, newManager]);
          return { success: true, message: 'Gerente adicionado.' };
        }
        return { success: false, message: 'Erro ao adicionar gerente.' };
      },
      removeManager: async (id) => { 
        await api.managers.delete(id);
        setManagers(prev => prev.filter(m => m.id !== id));
      },
      getManagers: () => managers,
      logManagerAction: async (action, managerName) => {
        if (user?.companyId) {
            const newLog = await api.auditLogs.create(action, managerName, user.companyId);
            if (newLog) {
                setAuditLogs(prev => [newLog, ...prev].slice(0, 3));
            }
        }
      },
      toggleCompanyStatus: async (id, s) => {
        const companyToUpdate = allCompanies.find(c => c.id === id);
        if (companyToUpdate) {
            const savedCompany = await api.companies.save({ ...companyToUpdate, subscriptionStatus: s });
            if (!savedCompany) return false;
            setAllCompanies(prev => prev.map(c => c.id === id ? savedCompany : c));
            setCurrentCompany(prev => prev?.id === id ? savedCompany : prev);
            return true;
        }
        return false;
      },
      updateCompanyBillingDate: async (id, d, activate) => {
        const comp = allCompanies.find(c => c.id === id);
        if(comp) {
            const updates: Partial<Company> = { nextBillingDate: d };
            if (activate) updates.subscriptionStatus = SubscriptionStatus.ACTIVE;
            const savedCompany = await api.companies.save({ ...comp, ...updates });
            if (!savedCompany) return false;
            setAllCompanies(prev => prev.map(c => c.id === id ? savedCompany : c));
            setCurrentCompany(prev => prev?.id === id ? savedCompany : prev);
            return true;
        }
        return false;
      },
      deleteCompany: async (id) => { 
        await api.companies.delete(id); 
        setAllCompanies(prev => prev.filter(c => c.id !== id));
      },
      adminUpdatePassword: async (id, p) => {
        const users = await api.users.getUsersByCompany(id);
        const owner = users?.find((u: User) => u.role === UserRole.OWNER);
        if (owner) return !!(await api.users.save({...owner, password: p}));
        return false;
      },
      createAdmin: async (n, e, p, l, canSendEmails = false, canLeadTeam = false) => {
        const existing = await api.users.getByEmail(e).catch(() => null);
        if (existing) return { success: false, message: 'Email já em uso.' };
        return !!(await api.users.save({ id: `admin-${crypto.randomUUID()}`, name: n, email: e, password: p, role: UserRole.SAAS_ADMIN, adminLevel: l, canSendEmails, canLeadTeam }))
            ? { success: true } : { success: false, message: 'Falha ao salvar.' };
      },
      deleteAdmin: async (id) => { await api.users.delete(id); },
      addProduct: async (p) => { 
        if (user?.companyId) {
            const newProduct = await api.products.save({ ...p, companyId: user.companyId });
            if (newProduct) setProducts(prev => [...prev, { ...p, ...newProduct }]);
            else if (isLocalTestMode()) setProducts(prev => [...prev, p]);
        }
      },
      updateProduct: async (p) => {
        if (user?.companyId) {
            const updatedProduct = await api.products.save({ ...p, companyId: user.companyId });
            if (updatedProduct) setProducts(prev => prev.map(prod => prod.id === p.id ? { ...p, ...updatedProduct } : prod));
            else if (isLocalTestMode()) setProducts(prev => prev.map(prod => prod.id === p.id ? p : prod));
        }
      },
      deleteProduct: async (id) => { 
        await api.products.delete(id); 
        setProducts(prev => prev.filter(p => p.id !== id));
      },
      updateProductStock: async (id, quantity, lowStockThreshold) => {
        if (!user?.companyId) return;
        const updatedProduct = await api.products.updateStock(id, quantity, lowStockThreshold);
        if (updatedProduct) setProducts(prev => prev.map(p => p.id === id ? { ...p, ...updatedProduct } : p));
      },
      addProductStockBatch: async (items) => {
        if (!user?.companyId) return false;
        const result = await api.products.addStockBatch(user.companyId, items);
        if (!result?.updatedProducts || result.updatedProducts.length !== items.length) return false;
        const updatedById = new Map(result.updatedProducts.map(product => [product.id, product]));
        setProducts(previous => previous.map(product => updatedById.has(product.id) ? { ...product, ...updatedById.get(product.id) } : product));
        return true;
      },
      addCategory: async (name) => { 
        if (user?.companyId) {
            const newCategory = await api.categories.save(name, user.companyId);
            if(newCategory) setCategories(prev => Array.from(new Set([...prev, name])).sort());
        }
      },
      deleteCategory: async (name) => {
        if (products.some(p => p.category === name)) return { success: false, message: 'Mova os produtos desta categoria primeiro.' };
        if (user?.companyId) {
            await api.categories.delete(name, user.companyId)
            setCategories(prev => prev.filter(c => c !== name));
        };
        return { success: true, message: '' };
      },
      updateCompanySettings: async (name, document, contactPhone) => {
        if (currentCompany) {
          const updatedCompany = await api.companies.updateSettings(currentCompany.id, { name, document, contactPhone });
          if (updatedCompany) setCurrentCompany(updatedCompany);
          return !!updatedCompany;
        }
        return false;
      },
      checkSubscriptionStatus: async () => { await refreshData(); },
      updateShiftSettings: async (settings) => {
        if (!user?.companyId) return false;
        const result = await api.companies.updateSettings(user.companyId, { 
            shiftEndHour: settings.hour, 
            shiftEndMinute: settings.minute,
            shiftStartHour: settings.startHour,
            shiftStartMinute: settings.startMinute,
            useShiftManagement: settings.useShift,
        });
        if (result) {
            setCurrentCompany(prev => prev ? ({ ...prev, ...result }) : null);
            return true;
        }
        return false;
      },
      updateFeatureSettings: async (settings) => {
        if (!user?.companyId) return false;

        if (isLocalTestMode()) {
            const { useSubLogin, ...localSettings } = settings;
            saveLocalCompanyOverrides(user.companyId, localSettings);

            if (useSubLogin !== undefined) {
                const savedCompany = await api.companies.updateSettings(user.companyId, { useSubLogin });
                if (!savedCompany) return false;
                setCurrentCompany(prev => prev ? ({ ...prev, ...localSettings, ...savedCompany }) : savedCompany);
                return true;
            }

            setCurrentCompany(prev => prev ? ({ ...prev, ...localSettings }) : null);
            console.warn('[Local Test] Configuração salva apenas no navegador local.');
            return true;
        }

        const result = await api.companies.updateSettings(user.companyId, settings);
        if (result) {
            setCurrentCompany(prev => prev ? ({ ...prev, ...result }) : null);
            return true;
        }

        return false;
      },
      openCashier: async (openingFloat, openingDetails) => {
        if (!user?.companyId) return false;
        const newShift = await api.cashierShifts.open(user.companyId, openingFloat, openingDetails);
        if (newShift) {
            setCashierShift(newShift);
            return true;
        }
        return false;
      },
      addCashMovement: async (movement) => {
        if (!cashierShift) return false;
        const updatedShift = await api.cashierShifts.addMovement(cashierShift.id, movement);
        if (updatedShift) {
            setCashierShift(updatedShift);
            return true;
        }
        return false;
      },
      closeCashier: async (report) => {
        if (!cashierShift) return false;
        const closedShift = await api.cashierShifts.close(cashierShift.id, report);
        if (closedShift) {
            setCashierShift({ status: 'CLOSED' } as any);
            await refreshData();
            return true;
        }
        return false;
      },
      updateClosedCashierReport: async (shiftId, finalReport) => {
        const updatedShift = await api.cashierShifts.updateFinalReport(shiftId, finalReport);
        return Boolean(updatedShift);
      },
      getCashierHistory: async () => {
        if (!user?.companyId) return [];
        const history = await api.cashierShifts.getHistory(user.companyId);
        return history || [];
      },
    }}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => {
  const context = useContext(StoreContext);
  if (!context) throw new Error('useStore error');
  return context;
};





